// NAV Update Service
// Handles updating Aggregated Pool NAV and distributing to all Series proportionally

import { IBFlexQueryData } from './ibFlexQueryParser';
import { getAllSeries, updateSeries } from './seriesService';
import { recordNAVHistory } from './navService';
import { collection, doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const POOL_NAV_COLLECTION = 'poolNAV';

export interface PoolNAVUpdate {
  totalNAV: number;
  date: Date;
  accountId: string;
  currency: string;
  mtmChange: number;
  updatedAt: Date;
  source: 'flex_query' | 'manual' | 'api';
}

/**
 * Get current Aggregated Pool NAV
 */
export const getCurrentPoolNAV = async (): Promise<number> => {
  try {
    const poolDoc = doc(db, POOL_NAV_COLLECTION, 'current');
    const snapshot = await getDoc(poolDoc);
    
    if (snapshot.exists()) {
      return snapshot.data().totalNAV || 0;
    }
    
    // If no pool NAV exists, calculate from series
    const series = await getAllSeries();
    const totalFromSeries = series.reduce((sum, s) => sum + (s.currentNAV || s.principalAmount), 0);
    return totalFromSeries;
  } catch (error) {
    console.error('Error getting current pool NAV:', error);
    return 0;
  }
};

/**
 * Update Aggregated Pool NAV from IB Flex Query data
 */
export const updatePoolNAVFromFlexQuery = async (
  flexData: IBFlexQueryData
): Promise<{
  success: boolean;
  oldNAV: number;
  newNAV: number;
  seriesUpdated: number;
  error?: string;
}> => {
  try {
    const newPoolNAV = flexData.endingNAV;
    const oldPoolNAV = await getCurrentPoolNAV();

    // Get all active series
    const allSeries = await getAllSeries();
    const activeSeries = allSeries.filter(s => s.status === 'active');

    if (activeSeries.length === 0) {
      throw new Error('No active series found to update');
    }

    // Calculate total current NAV of all series (before update)
    const totalSeriesNAV = activeSeries.reduce(
      (sum, s) => sum + (s.currentNAV || s.principalAmount),
      0
    );

    // If there's no existing NAV, use principal amounts
    const baseNAV = totalSeriesNAV > 0 ? totalSeriesNAV : oldPoolNAV;

    if (baseNAV === 0) {
      throw new Error('Cannot update NAV: base NAV is zero');
    }

    // Calculate proportional change ratio
    const changeRatio = newPoolNAV / baseNAV;

    // Update each series proportionally
    let seriesUpdated = 0;
    for (const series of activeSeries) {
      const currentSeriesNAV = series.currentNAV || series.principalAmount;
      const newSeriesNAV = currentSeriesNAV * changeRatio;

      await updateSeries(series.id, {
        currentNAV: newSeriesNAV,
        updatedAt: new Date(),
      });

      // Record NAV history for this series
      await recordNAVHistory({
        seriesId: series.id,
        type: 'series',
        totalNAV: newSeriesNAV,
        date: flexData.date,
        source: 'flex_query',
      });

      seriesUpdated++;
    }

    // Update pool NAV document
    const poolUpdate: PoolNAVUpdate = {
      totalNAV: newPoolNAV,
      date: flexData.date,
      accountId: flexData.accountId,
      currency: flexData.currency,
      mtmChange: flexData.mtmChange,
      updatedAt: new Date(),
      source: 'flex_query',
    };

    await setDoc(doc(db, POOL_NAV_COLLECTION, 'current'), {
      ...poolUpdate,
      date: Timestamp.fromDate(poolUpdate.date),
      updatedAt: Timestamp.fromDate(poolUpdate.updatedAt),
    });

    // Record pool NAV history
    await recordNAVHistory({
      type: 'aggregated_pool',
      totalNAV: newPoolNAV,
      date: flexData.date,
      source: 'flex_query',
      changeAmount: flexData.mtmChange,
      changePercent: baseNAV > 0 ? (flexData.mtmChange / baseNAV) * 100 : 0,
    });

    return {
      success: true,
      oldNAV: oldPoolNAV,
      newNAV: newPoolNAV,
      seriesUpdated,
    };
  } catch (error: any) {
    console.error('Error updating pool NAV:', error);
    return {
      success: false,
      oldNAV: 0,
      newNAV: 0,
      seriesUpdated: 0,
      error: error.message || 'Failed to update NAV',
    };
  }
};

/**
 * Get last NAV update information
 */
export const getLastNAVUpdate = async (): Promise<PoolNAVUpdate | null> => {
  try {
    const poolDoc = doc(db, POOL_NAV_COLLECTION, 'current');
    const snapshot = await getDoc(poolDoc);
    
    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        totalNAV: data.totalNAV,
        date: data.date?.toDate() || new Date(),
        accountId: data.accountId || '',
        currency: data.currency || 'USD',
        mtmChange: data.mtmChange || 0,
        updatedAt: data.updatedAt?.toDate() || new Date(),
        source: data.source || 'manual',
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting last NAV update:', error);
    return null;
  }
};

/**
 * Validate NAV update before applying
 */
export const validateNAVUpdate = async (
  newNAV: number,
  date: Date
): Promise<{ valid: boolean; warning?: string }> => {
  try {
    const currentPoolNAV = await getCurrentPoolNAV();
    const lastUpdate = await getLastNAVUpdate();

    // Check if new NAV is reasonable (not negative)
    if (newNAV < 0) {
      return { valid: false, warning: 'NAV cannot be negative' };
    }

    // Check if new NAV is zero (unusual but possible)
    if (newNAV === 0) {
      return { valid: true, warning: 'Warning: New NAV is zero. Please verify this is correct.' };
    }

    // Check for large changes (>50% change)
    if (currentPoolNAV > 0) {
      const changePercent = Math.abs((newNAV - currentPoolNAV) / currentPoolNAV) * 100;
      if (changePercent > 50) {
        return {
          valid: true,
          warning: `Warning: Large NAV change detected (${changePercent.toFixed(1)}%). Please verify this is correct.`,
        };
      }
    }

    // Check if date is in the future
    if (date > new Date()) {
      return { valid: false, warning: 'NAV date cannot be in the future' };
    }

    // Check if date is before last update
    if (lastUpdate && date < lastUpdate.date) {
      return {
        valid: true,
        warning: `Warning: This NAV date (${date.toLocaleDateString()}) is before the last update (${lastUpdate.date.toLocaleDateString()}). NAV history may be out of order.`,
      };
    }

    return { valid: true };
  } catch (error) {
    console.error('Error validating NAV update:', error);
    return { valid: true, warning: 'Could not validate NAV update' };
  }
};
